import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';

export type AlertClickEvent = (btn: IAlertButton, event: MouseEvent) => void;

export interface IAlertButton {
  id?: string;
  text: string;
  primary?: boolean;
  /** Close the alert after the click handler runs */
  close?: boolean;
  click?: AlertClickEvent;
}

export interface IAlertData {
  title?: string;
  /** Message text, supports markdown so inline links can be used */
  message?: string;
  /** Open links in the markdown message in a new tab */
  openLinksInNewTab?: boolean;
  /** Optional action buttons rendered below the message. Close [X] is always shown */
  buttons?: IAlertButton[];
  /** Auto-dismiss after this many ms. Omit to keep the alert until the user closes it */
  autoClose?: number;
  /** Called when the alert is removed (close [X], a button with close:true, autoClose or closeAll) */
  onClose?: () => void;
  /** Extra css class applied to the alert host */
  class?: string;
}

export interface IAlert extends IAlertData {
  readonly id: number;
}

/**
 * Shows lightweight informational alerts (toasts) on top of the screen.
 * Alerts are rendered by <ui-alerts> which must be placed once in app.component.
 */
@Injectable({
  providedIn: 'root'
})
export class AlertService {
  private readonly list = signal<IAlert[]>([]);
  readonly alerts = this.list.asReadonly();

  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();
  private seq = 0;

  /** Show an alert. Pass a string for a message-only alert */
  show(data: IAlertData | string): IAlert {
    const alertData = typeof data === 'string' ? { message: data } : data;
    const alert: IAlert = { ...alertData, id: ++this.seq };
    this.list.update(a => [...a, alert]);

    if (this.isBrowser && alert.autoClose) {
      const timer = setTimeout(() => this.close(alert), alert.autoClose);
      this.timers.set(alert.id, timer);
    }
    return alert;
  }

  close(alert: IAlert) {
    const timer = this.timers.get(alert.id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(alert.id);
    }
    this.list.update(a => a.filter(item => item.id !== alert.id));
    alert.onClose?.();
  }

  closeAll() {
    this.timers.forEach(t => clearTimeout(t));
    this.timers.clear();
    const current = this.list();
    this.list.set([]);
    current.forEach(a => a.onClose?.());
  }
}
